import { useCallback } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { saveAs } from "file-saver";
import useNotification from "../../../hooks/newUXNotification";
import { registerDocumentDownloadAttempt } from "../../../redux/modules/documentRequired/actions";

const useDocumentDownload = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const notification = useNotification();
  const { contract } = useSelector((state) => state.initialSettings);

  const registerDownload = useCallback(
    (documentExecutionId) => {
      dispatch(
        registerDocumentDownloadAttempt(
          { opportunityId: contract.value, documentExecutionId },
          (error) => {
            notification.displayError(
              intl.formatMessage({ id: `api.error.${error}` }),
              "register-download-document-error"
            );
          }
        )
      );
    },
    [dispatch, contract.value, intl, notification]
  );

  const downloadSingleFile = useCallback(
    async (url, documentExecutionId) => {
      const defaultFilename = intl.formatMessage({
        id: "quick_preview.default_filename",
      });

      try {
        const response = await fetch(url, { method: "GET" });

        if (!response.ok)
          throw new Error(
            intl.formatMessage({ id: "quick_preview.download_failed" })
          );

        const blob = await response.blob();
        const urlPath = url.split("?")[0];
        let filename =
          urlPath.substring(urlPath.lastIndexOf("/") + 1) || defaultFilename;

        if (!filename.includes(".")) {
          const contentType = response.headers.get("content-type") || "";
          if (contentType.includes("pdf")) filename += ".pdf";
          else if (contentType.includes("image/"))
            filename += `.${contentType.split("/")[1]?.split(";")[0] || "jpg"}`;
        }

        saveAs(blob, filename);
      } catch (_) {
        // Fallback: força download via <a>
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", "");
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } finally {
        registerDownload(documentExecutionId);
      }
    },
    [intl, registerDownload]
  );

  /**
   * Arquivo único → (url: string, documentExecutionId: number)
   * Pacote        → (items: Array<{ urlForDownload, documentExecutionId }>)
   */
  const handleDownload = useCallback(
    async (fileOrItems, documentExecutionId) => {
      const isPackage = Array.isArray(fileOrItems);

      if (isPackage) {
        if (!fileOrItems.length) {
          notification.displayError(
            intl.formatMessage({ id: "api.error.-12007" }),
            "download-document-error-message"
          );
          return;
        }

        for (const item of fileOrItems) {
          await downloadSingleFile(
            item.urlForDownload,
            item.documentExecutionId
          );
        }
        return;
      }

      if (!fileOrItems) {
        notification.displayError(
          intl.formatMessage({ id: "api.error.-12007" }),
          "download-document-error-message"
        );
        return;
      }

      await downloadSingleFile(fileOrItems, documentExecutionId);
    },
    [intl, notification, downloadSingleFile]
  );

  return { handleDownload };
};

export default useDocumentDownload;
